import { useState, useCallback, useRef } from 'react';
import { analyticsApi } from '../services/analytics-api.service';
import type { ExportSection } from '../types/analytics.types';

const PII_SECTIONS: ExportSection[] = ['fa-clients', 'sp-clients'];

const triggerDownload = (blob: Blob, section: ExportSection) => {
  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `bquest-${section}-export-${timestamp}.csv`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

type UseCSVExportOptions = {
  onError?: (message: string, section: ExportSection) => void;
};

export const useCSVExport = (options?: UseCSVExportOptions) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const lastFailedRef = useRef<{
    section: ExportSection;
    params?: Record<string, string | number | boolean | undefined>;
  } | null>(null);

  const isPIIExport = useCallback((section: ExportSection): boolean => {
    return PII_SECTIONS.includes(section);
  }, []);

  const exportCSV = useCallback(
    async (
      section: ExportSection,
      params?: Record<string, string | number | boolean | undefined>,
    ) => {
      setIsExporting(true);
      setExportError(null);
      lastFailedRef.current = null;
      try {
        const blob = await analyticsApi.exportCSV(section, params);
        triggerDownload(blob, section);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Export failed';
        setExportError(message);
        lastFailedRef.current = { section, params };
        options?.onError?.(message, section);
      } finally {
        setIsExporting(false);
      }
    },
    [options],
  );

  const retryExport = useCallback(async () => {
    if (!lastFailedRef.current) return;
    const { section, params } = lastFailedRef.current;
    await exportCSV(section, params);
  }, [exportCSV]);

  const clearExportError = useCallback(() => {
    setExportError(null);
    lastFailedRef.current = null;
  }, []);

  return {
    exportCSV,
    isExporting,
    isPIIExport,
    exportError,
    clearExportError,
    retryExport,
  };
};
